import axios, { AxiosInstance, AxiosError } from "axios";
import {
  LoginRequest,
  AuthResponse,
  ApiResponse,
  StoreLocation,
  RoleDirectoryPayload,
  UserListPayload,
  CreateUserPayload,
  UpdateUserPayload,
  AdminUser,
} from "../types";

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If sending FormData, remove Content-Type so browser sets it with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      window.location.href = "/admin/login";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>("/api/auth/login", credentials);
    return response.data;
  },

  verify: async (): Promise<{ success: boolean }> => {
    const response = await api.post("/api/auth/verify");
    return response.data;
  },
};

// Store API
export const storeApi = {
  getAll: async (): Promise<ApiResponse<StoreLocation[]>> => {
    const response = await api.get<ApiResponse<StoreLocation[]>>("/api/stores");
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<StoreLocation>> => {
    const response = await api.get<ApiResponse<StoreLocation>>(`/api/stores/${id}`);
    return response.data;
  },

  create: async (data: Partial<StoreLocation>): Promise<ApiResponse<StoreLocation>> => {
    const response = await api.post<ApiResponse<StoreLocation>>("/api/stores", data);
    return response.data;
  },

  update: async (id: number, data: Partial<StoreLocation> | FormData): Promise<ApiResponse<StoreLocation>> => {
    const response = await api.put<ApiResponse<StoreLocation>>(`/api/stores/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<StoreLocation>> => {
    const response = await api.delete<ApiResponse<StoreLocation>>(`/api/stores/${id}`);
    return response.data;
  },
};

export const roleApi = {
  getDirectory: async (): Promise<ApiResponse<RoleDirectoryPayload>> => {
    const response = await api.get<ApiResponse<RoleDirectoryPayload>>("/api/admin/roles");
    return response.data;
  },
};

export const userApi = {
  list: async (params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ApiResponse<UserListPayload>> => {
    const response = await api.get<ApiResponse<UserListPayload>>("/api/admin/users", {
      params,
    });
    return response.data;
  },
  create: async (payload: CreateUserPayload): Promise<ApiResponse<AdminUser>> => {
    const response = await api.post<ApiResponse<AdminUser>>("/api/admin/users", payload);
    return response.data;
  },
  update: async (id: number, payload: UpdateUserPayload): Promise<ApiResponse<AdminUser>> => {
    const response = await api.put<ApiResponse<AdminUser>>(`/api/admin/users/${id}`, payload);
    return response.data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await api.delete<ApiResponse<null>>(`/api/admin/users/${id}`);
    return response.data;
  },
};

type DateRangeParams = {
  range: string;
  from?: string;
  to?: string;
};

export const catalogApi = {
  getCategories: async () => {
    const response = await api.get("/api/rista/categories");
    return response.data;
  },
  getItems: async (params?: Record<string, string | number | boolean>) => {
    const response = await api.get("/api/rista/items", { params });
    return response.data;
  },
  getItemById: async (id: number) => {
    const response = await api.get(`/api/rista/items/${id}`);
    return response.data;
  },
  updateItem: async (id: number, data: FormData | Record<string, any>) => {
    // The request interceptor automatically handles Content-Type for FormData
    const response = await api.patch(`/api/rista/items/${id}`, data);
    return response.data;
  },
  getSyncLogs: async (limit = 10) => {
    const response = await api.get("/api/rista/sync-logs", {
      params: { limit },
    });
    return response.data;
  },
  /**
   * Export catalog as CSV from backend (includes bot_root, bot_subcategory, bot_subtype).
   * Triggers a file download.
   */
  exportCsv: async (params?: {
    include_inactive?: boolean;
    include_unavailable?: boolean;
  }) => {
    const response = await api.get("/api/rista/items/export", {
      params: {
        include_inactive: true,
        include_unavailable: true,
        ...params,
      },
      responseType: "blob",
    });
    if (response.status < 200 || response.status >= 300) {
      const text = await (response.data as Blob).text();
      let message = "Export failed";
      try {
        const json = JSON.parse(text);
        if (json?.message) message = json.message;
        else if (json?.error) message = json.error;
      } catch {
        if (text) message = text;
      }
      throw new Error(message);
    }
    const blob = response.data as Blob;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `catalog-items-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  triggerManualSync: async () => {
    const response = await api.post(
      "/api/admin/catalog/sync",
      {},
      {
        // Manual sync can take longer than the default 10s API timeout,
        // so we allow up to 2 minutes before aborting the request.
        timeout: 120000,
      },
    );
    return response.data;
  },
};

// Store-specific item pricing API
export const storeItemApi = {
  getStoreItemsByItemId: async (itemId: number) => {
    const response = await api.get(`/api/store-items/by-item/${itemId}`);
    return response.data;
  },
  getStoreItemsByStoreId: async (storeId: number) => {
    const response = await api.get(`/api/store-items/by-store/${storeId}`);
    return response.data;
  },
  getStoreItem: async (storeId: number, itemId: number) => {
    const response = await api.get(`/api/store-items/${storeId}/${itemId}`);
    return response.data;
  },
  upsertStoreItem: async (data: {
    store_id: number;
    item_id: number;
    price: number;
    available: boolean;
  }) => {
    const response = await api.post("/api/store-items", data);
    return response.data;
  },
  updateStoreItem: async (
    storeId: number,
    itemId: number,
    data: { price?: number; available?: boolean },
  ) => {
    const response = await api.patch(`/api/store-items/${storeId}/${itemId}`, data);
    return response.data;
  },
  deleteStoreItem: async (storeId: number, itemId: number) => {
    const response = await api.delete(`/api/store-items/${storeId}/${itemId}`);
    return response.data;
  },
  batchUpsertStoreItems: async (
    itemId: number,
    storeItems: Array<{ store_id: number; price: number; available: boolean }>,
  ) => {
    const response = await api.post(`/api/store-items/batch/${itemId}`, {
      store_items: storeItems,
    });
    return response.data;
  },
};

export const analyticsApi = {
  getOverview: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/overview", {
      params,
    });
    return response.data;
  },
  getSessions: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/sessions", {
      params,
    });
    return response.data;
  },
  getMessages: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/messages", {
      params,
    });
    return response.data;
  },
  getPlugins: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/plugins", {
      params,
    });
    return response.data;
  },
  getQueries: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/queries", {
      params,
    });
    return response.data;
  },
  getLLM: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/llm", {
      params,
    });
    return response.data;
  },
  getButtonClicks: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/button-clicks", {
      params,
    });
    return response.data;
  },
  getTrends: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/usage/trends", {
      params,
    });
    return response.data;
  },
  getItemOrderCount: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/item-order-count", {
      params,
    });
    return response.data;
  },
  getItemOrderCountExport: async (params: DateRangeParams) => {
    const response = await api.get("/api/admin/analytics/item-order-count/export", {
      params,
      responseType: "blob",
    });
    const blob = response.data as Blob;
    const cd = response.headers["content-disposition"];
    const match = cd && /filename="?([^";\n]+)"?/.exec(cd);
    const name = (match && match[1]) || "item-order-count.csv";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const sessionApi = {
  list: async (page = 1, pageSize = 20) => {
    const limit = pageSize;
    const offset = (page - 1) * pageSize;
    const response = await api.get("/api/sessions", {
      params: { limit, offset },
    });
    return response.data;
  },
  getById: async (sessionId: string) => {
    const response = await api.get(`/api/sessions/${sessionId}`);
    return response.data;
  },
};

export const chatApi = {
  /**
   * Fetch more items from cached lookup results (for "Show More" feature)
   * @param trackingSessionId Session tracking ID
   * @param offset Number of items already shown
   * @param limit Number of additional items to fetch
   */
  getMoreItems: async (trackingSessionId: string, offset: number = 0, limit: number = 10) => {
    const response = await api.get("/api/chat/more-items", {
      params: { trackingSessionId, offset, limit },
    });
    return response.data;
  },

  /**
   * Record ITEM_ORDER_CLICKED analytics event when user clicks "Order Now" on an item card.
   * Fire-and-forget; does not throw.
   */
  trackOrderClick: (payload: {
    itemId: number;
    itemName?: string;
    sessionId?: string;
  }) => {
    api
      .post("/api/chat/track-order-click", payload)
      .catch(() => {});
  },
};

export default api;
